import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import { IAccountDetailsDTO } from '../../../api/accounts.dao';
import { connectAccountVM } from '../account.context';
import TableLoadingRow from '../../global/tables/table-loading-row';
import DefaultButton from '../../global/default-button/default-button';
import { ListLayout } from '../../global/tables/table-list-layout';
import { Paging } from '../../global/_paging/paging-container';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { SearchInput } from '../../global/search-input/search-input';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { AccountListItem } from './_account-list-item/account-list-item';

interface IProps {
    accountsList: IAccountDetailsDTO[];
    pagingVM: PagingViewModel;
    isLoading: boolean;
    searchQuery: string;
    searchUsers: (query: string) => void;
    resetSearchQuery: () => void;
    intl?: IntlShape;
}

const AccountListPresentation = mobxReact.observer(props => {
    const { accountsList, intl, resetSearchQuery, searchUsers, showCreateAccountForm, editAccount, isAccountEditFormShown, isLoading } = props;

    const title = intl.formatMessage(
        {
            id: `oe.accounts.accountsTitle`,
            description: 'Accounts title',
            defaultMessage: 'Accounts',
        });

    const thead = [
        intl.formatMessage(
            {
                id: 'oe.organization.organizationName',
                description: 'Organization Name title',
                defaultMessage: 'Organization Name',
            }),
        <div className='search-input-container'>
            <SearchInput
                resetSearch={resetSearchQuery}
                onChange={(e, value) => searchUsers(value)}
                withResetSearchButton
                withDebounce
            />
        </div>
    ];

    const hasEditPermission = hasPermission(Permission.EDIT_ACCOUNT);

    return (
        <div className='page-container'>
            <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                <h1 className='text-ellipsis'>{title}</h1>
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        { hasPermission(Permission.CREATE_NEW_ACCOUNT) && (
                            <DefaultButton
                                className='events-list__button-new'
                                modifier='fat'
                                text={intl.formatMessage(
                                    {
                                        id: `oe.accounts.newAccount`,
                                        description: 'New Account button',
                                        defaultMessage: '{prefix}New Account',
                                    },
                                    {
                                        prefix: '+ '
                                    }
                                )}
                                onClick = {() => showCreateAccountForm(intl)}
                            />
                        )}
                    </div>
                </div>
            </section>
            <section className='accounts-list fixed-list-layout'>
                <ListLayout thead={thead}>
                    {!!accountsList?.length ? accountsList.map((item: IAccountDetailsDTO) => {
                        return <AccountListItem
                                {...item}
                                key={item.accountId}
                                editAccount={editAccount}
                                isSelected={isAccountEditFormShown((item.accountId).toString(10))}
                                withEdit={hasEditPermission}
                            />;
                    }) : <TableLoadingRow colSpan={thead.length} contentLength={accountsList?.length} />}
                </ListLayout>
                {!isLoading && <Paging pagingVM={props.pagingVM}/>}
            </section>
        </div>
    );
});

const AccountList = injectIntl(connectAccountVM<IProps>(AccountListPresentation, vm => {

    return {
        accountsList: vm.getAccountsList(),
        pagingVM: vm.getPagingVM(),
        isLoading: vm.isLoading,
        searchQuery: vm.getSearchQuery(),
        searchUsers: vm.searchUsers,
        resetSearchQuery: vm.resetSearchQuery,
        showCreateAccountForm: vm.showCreateAccountForm,
        editAccount: vm.editAccount,
        isAccountEditFormShown: vm.isAccountEditFormShown,
    };
}));
export { AccountList, AccountListPresentation };
